/**

* Copyright 2023 Manzia Inc (https://www.manzia.com)

Coded by Manzia Inc

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useContext } from "react";

// react-router components
import { useLocation, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
// import MDInput from "components/MDInput";
// import MDBadge from "components/MDBadge";
// import MDButton from "components/MDButton";

// Material Dashboard 2 PRO React examples
import Breadcrumbs from "examples/Breadcrumbs";
// import NotificationItem from "examples/Items/NotificationItem";
import ProfileItem from "examples/Items/ProfileItem";

// Auth
import { AuthUserContext } from "App";

// Manzia
import { ManziaNavLinks, ManziaAuthState } from "appSettings";
import useAuth from "customhooks/useAuth";
import useLocalChatbotUser from 'customhooks/useLocalChatbotUser';
import TrackBalance from "layouts/common/TrackBalance";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarDesktopMenu,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 PRO React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
} from "context";
import LoadView from "layouts/common/LoadView";

function DashboardNavbar({ absolute, light, isMini }) {
  // User
  const authUser = useContext(AuthUserContext);
  const USERID = authUser?.sub;
  const { chatbot } = useLocalChatbotUser({ userId: USERID });

  // Navbar
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  const navigate = useNavigate();

  // Sign Out
  const { loading: signoutLoading, value: signoutData, command: authSignout } = useAuth(ManziaAuthState.signOut.key);

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    // (fixedNavbar && window.scrollY === 0) || !fixedNavbar
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, false);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  // const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);

  useEffect(() => {
    if (!signoutData) return;
    navigate("/", { replace: true });
  }, [signoutData]);

  // Handle menu actions
  const menuHandler = (titleKey) => {
    switch (titleKey) {
      case ManziaNavLinks.signOut.key:
        authSignout()();
        break;
      case ManziaNavLinks.support.key:
        navigate("/support");
        break;
      case ManziaNavLinks.settings.key:
        navigate("/settings/profile");
        break;
      case ManziaNavLinks.billing.key:
        navigate("/pricing");
        break;
      default:
        break;
    }
  };

  // Render the profile menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      {chatbot && <ProfileItem 
        icon={<Icon>payment</Icon>} 
        titleKey={ManziaNavLinks.billing.key}
        clickHandler={() => menuHandler(ManziaNavLinks.billing.key)} 
      />}
      {chatbot && <ProfileItem 
        icon={<Icon>settings</Icon>} 
        titleKey={ManziaNavLinks.settings.key}
        clickHandler={() => menuHandler(ManziaNavLinks.settings.key)} 
      />}
      {chatbot && <ProfileItem 
        icon={<Icon>help</Icon>} 
        titleKey={ManziaNavLinks.support.key}
        clickHandler={() => menuHandler(ManziaNavLinks.support.key)} 
      />}
      <ProfileItem 
        icon={<Icon>logout</Icon>} 
        titleKey={ManziaNavLinks.signOut.key}
        clickHandler={() => menuHandler(ManziaNavLinks.signOut.key)}
      />
    </Menu>
  );

  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
          <IconButton sx={navbarDesktopMenu} onClick={handleMiniSidenav} size="small" disableRipple>
            <Icon fontSize="medium" sx={iconsStyle}>
              {miniSidenav ? "menu_open" : "menu"}
            </Icon>
          </IconButton>
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>

            <TrackBalance />
            
            {signoutLoading && <LoadView loadingMessage="" />}
            <MDBox color={light ? "white" : "inherit"}>
              {/* <Link to="/authentication/sign-in/basic">
                <IconButton 
                  sx={navbarIconButton} 
                  size="small" 
                  disableRipple
                >
                  <Icon sx={iconsStyle}>account_circle</Icon>
                </IconButton>
              </Link> */}
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              {/* <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                onClick={handleConfiguratorOpen}
              >
                <Icon sx={iconsStyle}>settings</Icon>
              </IconButton> */}
              {/* <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}
              >
                <MDBadge badgeContent={9} color="error" size="xs" circular>
                  <Icon sx={iconsStyle}>notifications</Icon>
                </MDBadge>
              </IconButton> */}
              <IconButton 
                  sx={navbarIconButton} 
                  size="large" 
                  disableRipple
                  color="inherit"
                  aria-controls="profile-menu"
                  aria-haspopup="true"
                  variant="contained"
                  onClick={handleOpenMenu}
                >
                  <Icon sx={iconsStyle}>account_circle</Icon>
                </IconButton>
              {renderMenu()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
